import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import type {
  MemberActivityPage,
  MemberActivityPreview,
  MemberActivityReceipt,
  StaffMemberActivity,
  StaffMemberActivityDetail,
} from '@pokerroom/types';
export default function MemberActivitiesPage() {
  const revision = useAuthStore((s) => s.sessionRevision);
  return <MemberActivitiesWorkspace key={revision} revision={revision} />;
}
function MemberActivitiesWorkspace({ revision }: { revision: number }) {
  const { t } = useTranslation('member'),
    qc = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions) ?? [];
  const canRead = actions.includes('member:read'),
    canImport = canRead && actions.includes('member:activities:import'),
    canPublish = canRead && actions.includes('member:activities:publish');
  const [sourceText, setSourceText] = useState(''),
    [preview, setPreview] = useState<MemberActivityPreview | null>(null);
  const [reviewed, setReviewed] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string | null>(null),
    [cursor, setCursor] = useState<string | null>(null),
    [resultCursor, setResultCursor] = useState<string | null>(null),
    [reason, setReason] = useState('');
  const uploadEpoch = useRef(0);
  const pending = useRef<{ kind: string; key: string; body: unknown } | null>(null);
  const list = useQuery({
    queryKey: ['member-activities', revision, cursor],
    queryFn: () =>
      api.get<MemberActivityPage<StaffMemberActivity>>('/member-activities', {
        params: { ...(cursor ? { cursor } : {}) },
      }),
    enabled: canRead,
  });
  const detail = useQuery({
    queryKey: ['member-activities-detail', revision, selected, resultCursor],
    queryFn: () =>
      api.get<StaffMemberActivityDetail>(`/member-activities/${selected}`, {
        params: { ...(resultCursor ? { cursor: resultCursor } : {}) },
      }),
    enabled: canRead && !!selected,
  });
  async function choose(file?: File) {
    const epoch = ++uploadEpoch.current;
    setError('');
    setSourceText('');
    setPreview(null);
    setReviewed(false);
    pending.current = null;
    if (!file) return;
    if (file.size > 1048576) {
      setError(t('activitiesFileError'));
      return;
    }
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
      if (epoch === uploadEpoch.current) setSourceText(text);
    } catch {
      if (epoch === uploadEpoch.current) setError(t('activitiesFileError'));
    }
  }
  async function previewFile() {
    if (busy) return;
    setBusy(true);
    setError('');
    setPreview(null);
    setReviewed(false);
    pending.current = null;
    try {
      setPreview(
        await api.post<MemberActivityPreview>('/member-activities/import/preview', { sourceText }),
      );
    } catch {
      setError(t('activitiesRequestError'));
    } finally {
      setBusy(false);
    }
  }
  async function write(kind: 'IMPORT' | 'PUBLISH' | 'WITHDRAW') {
    if (busy) return;
    const a = detail.data?.activity;
    if (kind === 'IMPORT' && (!preview || !reviewed || preview.errors.length || !canImport)) return;
    if (kind !== 'IMPORT' && (!a || !canPublish)) return;
    if (kind === 'WITHDRAW' && (!reason.trim() || !window.confirm(t('activitiesWithdrawWarning'))))
      return;
    const payload =
      kind === 'IMPORT'
        ? { sourceText, previewDigest: preview!.previewDigest }
        : kind === 'PUBLISH'
          ? { expectedVersion: a!.version, contentSha256: a!.contentSha256 }
          : { expectedVersion: a!.version, reason };
    // Keep the exact operation identity after a lost response. A changed payload starts a new operation.
    const signature = JSON.stringify(payload);
    if (
      !pending.current ||
      pending.current.kind !== kind ||
      JSON.stringify(pending.current.body) !== signature
    )
      pending.current = { kind, key: crypto.randomUUID(), body: payload };
    const operation = pending.current;
    setBusy(true);
    setError('');
    try {
      const path =
        kind === 'IMPORT'
          ? '/member-activities/import'
          : `/member-activities/${a!.id}/${kind === 'PUBLISH' ? 'publish' : 'withdraw'}`;
      const response = await api.post<MemberActivityReceipt>(path, {
        ...payload,
        operationKey: operation.key,
      });
      setSelected(response.activityId);
      setResultCursor(null);
      setReason('');
      pending.current = null;
      if (kind === 'IMPORT') {
        setSourceText('');
        setPreview(null);
        setReviewed(false);
      }
      await qc.invalidateQueries({ queryKey: ['member-activities'] });
      await qc.invalidateQueries({ queryKey: ['member-activities-detail'] });
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      setError(t(status === 409 ? 'activitiesConflict' : 'activitiesRequestError'));
      if (status === 409) {
        pending.current = null;
        await qc.invalidateQueries({ queryKey: ['member-activities-detail'] });
      }
    } finally {
      setBusy(false);
    }
  }
  if (!canRead)
    return (
      <AdminPage header={<AdminPageHeader title={t('activitiesTitle')} />}>
        {' '}
        <p role="alert">{t('activitiesDenied')}</p>{' '}
      </AdminPage>
    );
  const a = detail.data?.activity;
  return (
    <AdminPage
      className="admin-page-body space-y-4"
      aria-label={t('activitiesTitle')}
      header={
        <AdminPageHeader
          title={t('activitiesTitle')}
          back={
            <Link to="/members" className="link">
              {t('activitiesBack')}
            </Link>
          }
        />
      }
    >
      <p>{t('activitiesHistorical')}</p>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
      {canImport && (
        <div className="card bg-base-200 p-4 space-y-3">
          <label>
            {t('activitiesFile')}
            <input
              aria-label={t('activitiesFile')}
              type="file"
              accept="application/json,.json"
              disabled={busy}
              onChange={(e) => void choose(e.target.files?.[0])}
            />
          </label>
          <p className="text-sm">{t('activitiesFileHelp')}</p>
          <button className="btn" disabled={busy || !sourceText} onClick={() => void previewFile()}>
            {t('activitiesPreview')}
          </button>
          {preview && (
            <div className="space-y-2">
              <h2 className="font-semibold">{preview.document.activity.title}</h2>
              <p className="whitespace-pre-wrap">{preview.document.activity.terms}</p>
              <p>{preview.document.externalDrawRef}</p>
              <p>
                {preview.document.activity.startsAt} – {preview.document.activity.endsAt} (
                {preview.document.activity.timezone})
              </p>
              <p className="break-all">
                {t('activitiesSourceHash')}: {preview.sourceSha256}
              </p>
              <p>
                {t('activitiesRows')}: {preview.document.results.length}
              </p>
              <div className="max-h-72 overflow-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('activitiesResultRef')}</th>
                      <th>{t('activitiesMemberId')}</th>
                      <th>{t('activitiesOutcome')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.document.results.map((r) => (
                      <tr key={r.externalResultRef}>
                        <td>{r.externalResultRef}</td>
                        <td>{r.memberId}</td>
                        <td>{r.outcome}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.errors.map((e) => (
                <p role="alert" key={e.row} className="text-error">
                  {t('activitiesRowError')}: {e.row} — {e.code}
                </p>
              ))}
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                />
                {t('activitiesReviewConfirm')}
              </label>
              <button
                className="btn btn-primary"
                disabled={busy || !reviewed || preview.errors.length > 0}
                onClick={() => void write('IMPORT')}
              >
                {t('activitiesImportDraft')}
              </button>
            </div>
          )}
        </div>
      )}
      {list.isPending && <p role="status">{t('activitiesLoading')}</p>}
      {list.isError && (
        <div role="alert">
          <p>{t('activitiesRequestError')}</p>
          <button className="btn" onClick={() => void list.refetch()}>
            {t('activitiesRetry')}
          </button>
        </div>
      )}
      <ul className="space-y-2">
        {list.data?.items.map((item) => (
          <li key={item.id}>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setSelected(item.id);
                setResultCursor(null);
                setReason('');
                pending.current = null;
              }}
            >
              {item.title} — {t('activitiesState' + item.state)}
            </button>
          </li>
        ))}
      </ul>
      {list.data?.items.length === 0 && <p>{t('activitiesEmpty')}</p>}
      {list.data?.nextCursor && (
        <button className="btn" onClick={() => setCursor(list.data!.nextCursor)}>
          {t('activitiesMore')}
        </button>
      )}
      {cursor && (
        <button className="btn" onClick={() => setCursor(null)}>
          {t('activitiesFirstPage')}
        </button>
      )}
      {selected && detail.isPending && <p role="status">{t('activitiesLoading')}</p>}
      {detail.isError && (
        <div role="alert">
          <p>{t('activitiesRequestError')}</p>
          <button className="btn" onClick={() => void detail.refetch()}>
            {t('activitiesRetry')}
          </button>
        </div>
      )}
      {a && (
        <article className="card bg-base-200 p-4 space-y-3">
          <h2 className="text-lg font-semibold">{a.title}</h2>
          <p>
            {t('activitiesState' + a.state)} · {t('activitiesVersion')}: {a.version}
          </p>
          <p>{a.externalDrawRef}</p>
          <p className="whitespace-pre-wrap">{a.terms}</p>
          <p>
            {a.startsAt} – {a.endsAt} ({a.timezone})
          </p>
          <p className="break-all">
            {t('activitiesSourceHash')}: {a.sourceSha256}
          </p>
          <p>
            {t('activitiesRows')}: {a.resultCount}
          </p>
          <div className="overflow-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('activitiesResultRef')}</th>
                  <th>{t('activitiesMemberId')}</th>
                  <th>{t('activitiesOutcome')}</th>
                  <th>{t('activitiesRecordedClaim')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.data!.results.items.map((r) => (
                  <tr key={r.id}>
                    <td>{r.externalResultRef}</td>
                    <td>{r.memberId}</td>
                    <td>
                      {r.outcome}
                      {r.prizeLabel && <span> — {r.prizeLabel}</span>}
                    </td>
                    <td>
                      {r.claimStatus} ({r.observedAt})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {detail.data?.results.nextCursor && (
            <button
              className="btn"
              onClick={() => setResultCursor(detail.data!.results.nextCursor)}
            >
              {t('activitiesMore')}
            </button>
          )}
          {resultCursor && (
            <button className="btn" onClick={() => setResultCursor(null)}>
              {t('activitiesFirstPage')}
            </button>
          )}
          {canPublish && a.state === 'DRAFT' && (
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void write('PUBLISH')}
            >
              {t('activitiesPublish')}
            </button>
          )}
          {canPublish && a.state !== 'WITHDRAWN' && (
            <div className="space-y-2">
              <label>
                {t('activitiesWithdrawReason')}
                <input
                  className="input input-bordered w-full"
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <p>{t('activitiesWithdrawWarning')}</p>
              <button
                className="btn btn-warning"
                disabled={busy || !reason.trim()}
                onClick={() => void write('WITHDRAW')}
              >
                {t('activitiesWithdraw')}
              </button>
            </div>
          )}
          {a.withdrawReason && (
            <p>
              {t('activitiesWithdrawReason')}: {a.withdrawReason}
            </p>
          )}
        </article>
      )}
    </AdminPage>
  );
}
